/**
 * `/metal-resources/metal-market-intelligence/podcast/`: the Cup O' Joe podcast landing page.
 * It lists every episode, newest first, each linking to its episode page. Ryerson build only.
 *
 * `getStaticProps` runs once at build. It fetches the header and footer data and the episode list
 * (`src/contentful/podcast/getContentfulPodcastEpisodesData.tsx` →
 * `getContentfulPodcastEpisodesData`), and `components/Blog/Podcasts.tsx` renders the list. A
 * Contentful failure fails the build (web-architecture ADR-0033). New episodes appear only after
 * the next build.
 *
 * Related:
 * - `./[slug].tsx`: the episode pages this list links to.
 * - `components/Blog/CupOJoe.tsx`: the Metal Market Intelligence landing's teaser that links here.
 * - `components/Blog/Podcast.content.tsx` → `MetaObject`: an identical copy of this page's
 *   `MetaObject` that nothing imports.
 */
import React from "react";
import { useApplication } from "@ryerson/frontend.application";
import MetaHead, { MetaContent } from "@components/Shared/model/MetaHead";
import { LanguageContent } from "@components/Shared/model/LanguageContent";
import Podcasts from "@components/Blog/Podcasts";
import { allContentfulHeaderCalls, HeaderPageProps } from "@ryerson/frontend.contentful";
import HeaderLayout from "@components/NextLayouts/HeaderLayout";
import { getHeaderData } from "@components/NextLayouts/nextUtilities";
import { getContentfulPodcastEpisodesData } from "@contentful/podcast/getContentfulPodcastEpisodesData";
import { TranslatedPodcastNodes } from "@contentful/podcast/types";

/** The landing page's SEO head. `fr` and `es` repeat the English title and description. */
const MetaObject: LanguageContent<MetaContent> = {
	en: {
		title: "Cup O' Joe - A Metal Podcast - Ryerson",
		description: `Welcome to Cup o’ Joe, your go-to podcast for a fun and informative look at the dynamic world of metals!`,
		url: `/metal-resources/metal-market-intelligence/podcast`,
		company: false,
	},
	fr: {
		title: "Cup O' Joe - A Metal Podcast - Ryerson",
		description: `Welcome to Cup o’ Joe, your go-to podcast for a fun and informative look at the dynamic world of metals!`,
		url: `/metal-resources/metal-market-intelligence/podcast`,
		company: false,
	},
	es: {
		title: "Cup O' Joe - A Metal Podcast - Ryerson",
		description: `Welcome to Cup o’ Joe, your go-to podcast for a fun and informative look at the dynamic world of metals!`,
		url: `/metal-resources/metal-market-intelligence/podcast`,
		company: false,
	},
};

type PodcastProps = {
	podcastData: TranslatedPodcastNodes;
};

/**
 * Builds the page on the Ryerson build only (`notFound` on any other brand). Returns the shared
 * header and footer props plus `podcastData`, the episode list per language.
 *
 * @throws The Contentful or network error from either fetch, which fails the build.
 */
export const getStaticProps = async () => {
	if (process.env.NEXT_PUBLIC_COMPANY !== "ryerson") {
		return { notFound: true }; // tells Next.js to skip page generation and return 404
	}

	let { props: headerProps } = await allContentfulHeaderCalls();
	let podcastData = await getContentfulPodcastEpisodesData();
	return { props: { ...headerProps, podcastData } };
};

/** The podcast landing page: SEO head for the visitor's language, then the episode list. */
const Page: React.FC<HeaderPageProps & PodcastProps> = ({ podcastData, ...props }) => {
	const {
		localization: { language },
	} = useApplication();

	return (
		<HeaderLayout {...getHeaderData(props)}>
			<MetaHead content={MetaObject[language]} />
			<Podcasts podcastContentfulData={podcastData} />
		</HeaderLayout>
	);
};

export default Page;
